import React, { useEffect, useMemo, useState } from 'react';
import { processAdminDeluxePayment } from '../services/payments/deluxeService';
import DeluxeEmbeddedPaymentForm from '../../deluxe/embedded/DeluxeEmbeddedPaymentForm';
import { getPlatformDeluxeHostedFormConfig } from './app-settings/deluxe/deluxeCredentialsApi';
import '../styles/authorizeManualCharge.css';
import { fetchTransactions } from '../services/transactions/transactionsService';
import { useAdminAuth } from '../context/AdminAuthContext';

const AuthorizeManualCharge = () => {
	const { admin } = useAdminAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [txLoading, setTxLoading] = useState(true);
	const [txItems, setTxItems] = useState([]);
	const [txCursor, setTxCursor] = useState(null);
	const [txStatus, setTxStatus] = useState('all');
	const [form, setForm] = useState({
		amount: '',
		currency: 'USD',
		description: '',
		customerName: '',
		customerEmail: '',
	});
	const [hpfConfig, setHpfConfig] = useState(null);
	const [cryptogram, setCryptogram] = useState('');
	const [selectedTx, setSelectedTx] = useState(null);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const filteredTransactions = useMemo(
		() => (txStatus === 'all' ? txItems : txItems.filter((tx) => tx.status === txStatus)),
		[txItems, txStatus]
	);

	const formatCardNumber = (value) => value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ');
	const formatCurrencyAmount = (amount, currency = 'USD') => {
		if (amount === undefined || amount === null || amount === '') return '-';
		const numericAmount = Number(amount);
		if (Number.isNaN(numericAmount)) return `${amount}`;
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currency || 'USD',
		}).format(numericAmount);
	};
	const formatTransactionDate = (value) => (value ? new Date(value).toLocaleString() : '-');
	const getStatusClassName = (status) => {
		if (status === 'completed') return 'amc-status amc-status--success';
		if (status === 'failed') return 'amc-status amc-status--danger';
		if (status === 'error') return 'amc-status amc-status--warning';
		return 'amc-status';
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		if (name === 'cardNumber') {
			setForm((p) => ({ ...p, cardNumber: formatCardNumber(value) }));
			return;
		}
		if (name === 'expiryMonth') {
			const m = value.replace(/\D/g, '').slice(0, 2);
			if (m === '' || (parseInt(m) >= 1 && parseInt(m) <= 12)) setForm((p) => ({ ...p, expiryMonth: m }));
			return;
		}
		if (name === 'expiryYear') {
			setForm((p) => ({ ...p, expiryYear: value.replace(/\D/g, '').slice(0, 4) }));
			return;
		}
		if (name === 'cvv') {
			setForm((p) => ({ ...p, cvv: value.replace(/\D/g, '').slice(0, 4) }));
			return;
		}
		if (name === 'amount') {
			const amt = value.replace(/[^\d.]/g, '');
			setForm((p) => ({ ...p, amount: amt }));
			return;
		}
		setForm((p) => ({ ...p, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError(null);
		setSuccess(null);
		if (!cryptogram) {
			setError('Save the card in the Deluxe form first.');
			setLoading(false);
			return;
		}
		console.group('Deluxe: ManualCharge submit');
		console.log('Submit', {
			amount: form.amount,
			currency: form.currency,
		});
		try {
			const result = await processAdminDeluxePayment({
				amount: form.amount,
				cryptogram,
				currency: form.currency,
				customerName: form.customerName,
				customerEmail: form.customerEmail,
				description: form.description,
				performedByUid: admin?.uid,
				performedByEmail: admin?.email,
			});
			if (result?.success) {
				console.log('Charge success', {
					txId: result?.transaction?.id,
					amount: result?.transaction?.amount,
					currency: result?.transaction?.currency,
				});
				setSuccess(result.transaction);
				// Refresh transactions after a successful charge
				await refreshTransactions();
			} else {
				console.warn('Charge response not successful', {
					message: result?.message,
					code: result?.code,
				});
				setError(result?.message || 'Payment failed');
			}
		} catch (err) {
			console.error('Charge exception', {
				name: err?.name,
				code: err?.code,
				message: err?.message,
				details: err?.details,
			});
			setError(err?.message || 'Payment failed');
		} finally {
			setLoading(false);
			console.groupEnd();
		}
	};

	const refreshTransactions = async () => {
		setTxLoading(true);
		try {
			const res = await fetchTransactions({ pageSize: 25 });
			setTxItems(res.items);
			setTxCursor(res.cursor);
		} finally {
			setTxLoading(false);
		}
	};

	useEffect(() => {
		let ignore = false;
		(async () => {
			setTxLoading(true);
			try {
				const res = await fetchTransactions({ pageSize: 25 });
				if (!ignore) {
					setTxItems(res.items);
					setTxCursor(res.cursor);
				}
			} finally {
				if (!ignore) setTxLoading(false);
			}
		})();
		return () => { ignore = true; };
	}, []);

	const loadMoreTx = async () => {
		if (!txCursor) return;
		setTxLoading(true);
		try {
			const res = await fetchTransactions({ pageSize: 25, cursor: txCursor });
			setTxItems((prev) => [...prev, ...res.items]);
			setTxCursor(res.cursor);
		} finally {
			setTxLoading(false);
		}
	};

	const openTxDetails = (tx) => {
		setSelectedTx(tx);
		setDetailsOpen(true);
	};

	const closeTxDetails = () => {
		setDetailsOpen(false);
		setSelectedTx(null);
	};

	useEffect(() => {
		if (!detailsOpen) return undefined;
		const handleEscape = (event) => {
			if (event.key === 'Escape') closeTxDetails();
		};
		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [detailsOpen]);

	return (
		<div className="admin-page amc-root">
			<div className="admin-page__header">
				<div>
					<h1 className="admin-page__title">Manual Charge</h1>
					<p className="admin-page__subtitle">Charge one-time Deluxe card payments for customers.</p>
				</div>
			</div>
			<div className="amc-layout">
				<section className="amc-panel admin-card">
					{loading && (
						<div className="amc-loading-overlay" aria-hidden="true">
							<div className="amc-spinner" />
						</div>
					)}
					<form className="amc-form" onSubmit={handleSubmit}>
						<div className="amc-grid">
							<label className="amc-field">
								<span className="amc-field-label">Amount</span>
								<div className="amc-input-wrap amc-input-wrap--prefix">
									<span className="amc-input-prefix">$</span>
									<input
										className="amc-input"
										type="text"
										name="amount"
										value={form.amount}
										onChange={onChange}
										placeholder="0.00"
										required
										inputMode="decimal"
									/>
								</div>
							</label>

							<label className="amc-field">
								<span className="amc-field-label">Currency</span>
								<select className="amc-input" name="currency" value={form.currency} onChange={onChange}>
									<option value="USD">USD</option>
									<option value="CAD">CAD</option>
									<option value="EUR">EUR</option>
									<option value="GBP">GBP</option>
								</select>
							</label>

							<div className="amc-field amc-field--full">
								<span className="amc-field-label">Card</span>
								<AdminDeluxeCardForm
									onReady={setHpfConfig}
									onCryptogram={setCryptogram}
									hpfConfig={hpfConfig}
								/>
								{cryptogram ? <p className="amc-help">Card token ready. Click Charge to process.</p> : null}
							</div>
						</div>

						<div className="amc-divider" />

						<div className="amc-grid">
							<label className="amc-field">
								<span className="amc-field-label">Customer name</span>
								<input
									className="amc-input"
									type="text"
									name="customerName"
									value={form.customerName}
									onChange={onChange}
									placeholder="Optional"
								/>
							</label>

							<label className="amc-field">
								<span className="amc-field-label">Customer email</span>
								<input
									className="amc-input"
									type="email"
									name="customerEmail"
									value={form.customerEmail}
									onChange={onChange}
									placeholder="Optional"
								/>
							</label>

							<label className="amc-field amc-field--full">
								<span className="amc-field-label">Description</span>
								<input
									className="amc-input"
									type="text"
									name="description"
									value={form.description}
									onChange={onChange}
									placeholder="Optional"
								/>
							</label>
						</div>

						{error && (
							<div className="amc-alert amc-alert--error" role="alert">
								{error}
							</div>
						)}

						{success && (
							<div className="amc-alert amc-alert--success" role="status">
								Payment successful. Transaction ID: {success?.id} Amount: {formatCurrencyAmount(success?.amount, success?.currency)}
							</div>
						)}

						<div className="amc-actions">
							<button className="amc-button amc-button--primary" type="submit" disabled={loading}>
								{loading ? 'Processing...' : 'Charge now'}
							</button>
						</div>
					</form>
				</section>

				<section className="amc-transactions admin-card">
					<div className="amc-transactions-header">
						<div>
							<h2 className="amc-panel-title">Recent transactions</h2>
							<p className="amc-panel-subtitle">Review recent manual payment attempts and open a row for full details.</p>
						</div>
						<div className="amc-transactions-controls">
							<label className="amc-field amc-field--compact">
								<span className="amc-field-label">Status</span>
								<select className="amc-input" value={txStatus} onChange={(e) => setTxStatus(e.target.value)}>
									<option value="all">All</option>
									<option value="completed">Completed</option>
									<option value="failed">Failed</option>
									<option value="error">Error</option>
								</select>
							</label>
							<button className="amc-button amc-button--secondary" type="button" onClick={refreshTransactions} disabled={txLoading}>
								Refresh
							</button>
						</div>
					</div>

					<div className="amc-table-wrap">
						<table className="amc-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Transaction ID</th>
									<th className="amc-table__numeric">Amount</th>
									<th>Currency</th>
									<th>Status</th>
									<th>Gateway</th>
									<th>Source</th>
									<th>Performer</th>
									<th>Customer</th>
									<th>Description</th>
									<th>Code</th>
									<th>Message</th>
								</tr>
							</thead>
							<tbody>
								{filteredTransactions.length === 0 && !txLoading ? (
									<tr>
										<td className="amc-table__empty" colSpan={12}>No transactions</td>
									</tr>
								) : (
									filteredTransactions.map((tx) => (
										<tr key={tx.id || tx.createdAt} className="amc-table__row" onClick={() => openTxDetails(tx)}>
											<td>{formatTransactionDate(tx.createdAt)}</td>
											<td>{tx.id || '-'}</td>
											<td className="amc-table__numeric">{formatCurrencyAmount(tx.amount, tx.currency)}</td>
											<td>{tx.currency || '-'}</td>
											<td>
												<span className={getStatusClassName(tx.status)}>{tx.status || 'unknown'}</span>
											</td>
											<td>{tx.gateway || '-'}</td>
											<td>{tx.source || '-'}</td>
											<td>{tx.performedBy?.email || tx.performedBy?.uid || '-'}</td>
											<td>{tx.customer?.name || tx.customer?.email || '-'}</td>
											<td className="amc-table__truncate">{tx.description || '-'}</td>
											<td>{tx.code || '-'}</td>
											<td className="amc-table__truncate">{tx.message || '-'}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<div className="amc-table-footer">
						<button className="amc-button amc-button--secondary" type="button" onClick={loadMoreTx} disabled={!txCursor || txLoading}>
							{txLoading ? 'Loading...' : 'Load more'}
						</button>
						{txLoading && <div className="amc-inline-loading">Fetching transactions...</div>}
					</div>
				</section>
			</div>

			{detailsOpen && (
				<div className="amc-modal-backdrop" onClick={closeTxDetails} role="presentation">
					<div
						className="amc-modal"
						role="dialog"
						aria-modal="true"
						aria-labelledby="amc-details-title"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="amc-modal-header">
							<h2 id="amc-details-title" className="amc-panel-title">Transaction details</h2>
							<button className="amc-icon-button" type="button" onClick={closeTxDetails} aria-label="Close details">
								×
							</button>
						</div>
						<div className="amc-detail-grid">
							<div className="amc-detail-label">ID</div>
							<div className="amc-detail-value">{selectedTx?.id || '-'}</div>
							<div className="amc-detail-label">Date</div>
							<div className="amc-detail-value">{formatTransactionDate(selectedTx?.createdAt)}</div>
							<div className="amc-detail-label">Amount</div>
							<div className="amc-detail-value">{formatCurrencyAmount(selectedTx?.amount, selectedTx?.currency)}</div>
							<div className="amc-detail-label">Currency</div>
							<div className="amc-detail-value">{selectedTx?.currency || '-'}</div>
							<div className="amc-detail-label">Status</div>
							<div className="amc-detail-value">{selectedTx?.status || '-'}</div>
							<div className="amc-detail-label">Code</div>
							<div className="amc-detail-value">{selectedTx?.code || '-'}</div>
							<div className="amc-detail-label">Message</div>
							<div className="amc-detail-value amc-detail-value--break">{selectedTx?.message || '-'}</div>
							<div className="amc-detail-label">Gateway</div>
							<div className="amc-detail-value">{selectedTx?.gateway || '-'}</div>
							<div className="amc-detail-label">Source</div>
							<div className="amc-detail-value">{selectedTx?.source || '-'}</div>
							<div className="amc-detail-label">Auth code</div>
							<div className="amc-detail-value">{selectedTx?.authCode || '-'}</div>
							<div className="amc-detail-label">AVS</div>
							<div className="amc-detail-value">{selectedTx?.avsResponse || '-'}</div>
							<div className="amc-detail-label">CVV</div>
							<div className="amc-detail-value">{selectedTx?.cvvResponse || '-'}</div>
							<div className="amc-detail-label">Performer</div>
							<div className="amc-detail-value">{selectedTx?.performedBy?.email || selectedTx?.performedBy?.uid || '-'}</div>
							<div className="amc-detail-label">Customer</div>
							<div className="amc-detail-value">{selectedTx?.customer?.name || selectedTx?.customer?.email || '-'}</div>
							<div className="amc-detail-label">Description</div>
							<div className="amc-detail-value amc-detail-value--break">{selectedTx?.description || '-'}</div>
						</div>
						<div className="amc-modal-actions">
							<button className="amc-button amc-button--secondary" type="button" onClick={closeTxDetails}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const AdminDeluxeCardForm = ({ onReady, onCryptogram, hpfConfig }) => {
	const [error, setError] = useState('');

	useEffect(() => {
		let cancelled = false;
		getPlatformDeluxeHostedFormConfig({ environment: 'PRODUCTION' })
			.then((data) => {
				if (!cancelled) onReady(data);
			})
			.catch((err) => {
				if (!cancelled) setError(err?.message || 'Deluxe is not configured in App Settings');
			});
		return () => {
			cancelled = true;
		};
	}, [onReady]);

	if (error) return <div className="amc-error">{error}</div>;
	if (!hpfConfig) return <p>Loading Deluxe card form…</p>;

	return (
		<DeluxeEmbeddedPaymentForm
			jwt={hpfConfig.jwt}
			scriptUrl={hpfConfig.scriptUrl}
			mode="cryptogram"
			onSuccess={({ paymentId, cryptogram: token }) => onCryptogram(paymentId || token)}
			onError={(err) => setError(err?.message || 'Card form failed')}
		/>
	);
};

export default AuthorizeManualCharge;


